import { useCallback, useState } from 'react';
import { t, exists } from 'i18next';

import { ValidationErrors } from '../types';

type FormShape = Record<string, unknown>;

interface IFieldValidation {
  isValid: boolean;
  isTouched: boolean;
  errorMessage: string;
}

type ValidationForm<TFormValues extends FormShape> = {
  [K in keyof TFormValues]: IFieldValidation;
};

type FieldValidationResult = boolean | string;

export type CheckFieldValueFnMap<TFormValues extends FormShape> = {
  [K in keyof TFormValues]: (value: TFormValues[K], allFormValues: TFormValues) => FieldValidationResult;
};

const normalizeValidationResult = (result: FieldValidationResult) => ({
  isValid: result === true,
  errorMessage: typeof result === 'string' ? result : '',
});

export type UseValidationForm<TFormValues extends FormShape> = {
  validationState: ValidationForm<TFormValues>;
  onFieldChange: <K extends keyof TFormValues>(fieldName: K, value: TFormValues[K], touch?: boolean) => void;
  onFieldBlur: (fieldName: keyof TFormValues) => void;
  onSubmitErrors: (errors: ValidationErrors) => void;
  validate: () => boolean;
  reset: (newInitialValues?: TFormValues) => void;
  updateFormValues: (values: TFormValues) => void;
  getShowError: (field: IFieldValidation) => boolean;
  getErrorMessage: (field: IFieldValidation, defaultErrorMessage: string) => string;
};

export const useValidationForm = <TFormValues extends FormShape>(
  initialValues: TFormValues,
  checkFieldValueFnMap: CheckFieldValueFnMap<TFormValues>
): UseValidationForm<TFormValues> => {
  const [formValues, setFormValues] = useState<TFormValues>(initialValues);

  const createValidationState = useCallback(
    (currentValues: TFormValues, isTouched: boolean): ValidationForm<TFormValues> => {
      const state = {} as ValidationForm<TFormValues>;
      for (const key in checkFieldValueFnMap) {
        const validationResult = normalizeValidationResult(
          checkFieldValueFnMap[key](currentValues[key], currentValues)
        );
        state[key] = {
          isValid: validationResult.isValid,
          isTouched,
          errorMessage: validationResult.errorMessage,
        };
      }
      return state;
    },
    [checkFieldValueFnMap]
  );

  const [validationState, setValidationState] = useState<ValidationForm<TFormValues>>(() =>
    createValidationState(initialValues, false)
  );

  const onFieldChange = <K extends keyof TFormValues>(fieldName: K, value: TFormValues[K], touch?: boolean) => {
    const nextFormValues = {
      ...formValues,
      [fieldName]: value,
    };

    setFormValues(nextFormValues);

    setValidationState((prevState) => {
      const newState = { ...prevState } as ValidationForm<TFormValues>;

      for (const key in checkFieldValueFnMap) {
        const validationResult = normalizeValidationResult(
          checkFieldValueFnMap[key](nextFormValues[key], nextFormValues)
        );
        newState[key] = {
          ...prevState[key],
          isValid: validationResult.isValid,
          errorMessage:
            key === (fieldName as keyof TFormValues) ? validationResult.errorMessage : prevState[key].errorMessage,
          isTouched: key === (fieldName as keyof TFormValues) && touch !== false ? true : prevState[key].isTouched,
        };
      }

      return newState;
    });
  };

  const onFieldBlur = (fieldName: keyof TFormValues) => {
    setValidationState((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        isTouched: true,
      },
    }));
  };

  const getValidationErrorText = (errorType: string) =>
    // eslint-disable-next-line security/detect-non-literal-fs-filename
    exists(`API validation error: ${errorType}`)
      ? t(`API validation error: ${errorType}`)
      : t(`API validation error: Unknown`);

  const onSubmitErrors = (errors: ValidationErrors) => {
    setValidationState((prev) => {
      const next = { ...prev };
      Object.keys(errors).forEach((key) => {
        if (key in next) {
          next[key as keyof TFormValues] = {
            ...next[key as keyof TFormValues],
            isValid: false,
            errorMessage: getValidationErrorText(errors[key].type),
          };
        }
      });
      return next;
    });
  };

  const validate = (): boolean => {
    const currentValidationState = createValidationState(formValues, true);
    setValidationState(currentValidationState);
    return Object.values(currentValidationState).every((field) => field.isValid);
  };

  const reset = (newInitialValues?: TFormValues) => {
    const valuesToReset = newInitialValues ?? initialValues;
    setFormValues(valuesToReset);
    setValidationState(createValidationState(valuesToReset, false));
  };

  // NOTE: updates internally stored form values without resetting validation state.
  // Useful when form values are updated from parent component.
  const updateFormValues = (values: TFormValues) => {
    setFormValues(values);
  };

  const getShowError = (field: IFieldValidation) => field.isTouched && !field.isValid;

  const getErrorMessage = (field: IFieldValidation, defaultErrorMessage: string) => {
    if (!field.errorMessage) {
      return defaultErrorMessage;
    }

    return exists(field.errorMessage) ? t(field.errorMessage) : field.errorMessage;
  };

  return {
    validationState,
    onFieldChange,
    onFieldBlur,
    onSubmitErrors,
    validate,
    reset,
    updateFormValues,
    getShowError,
    getErrorMessage,
  };
};
